import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Phone,
  ShieldCheck,
  ArrowRight,
  Loader2,
  Sparkles,
  ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import {
  requestOtp,
  startGoogleLogin,
  verifyOtp,
} from "@/lib/customerAuth";

const OTP_LENGTH = 6;

export const CustomerAuthDialog = ({ open, onClose, onAuthed }) => {
  const [step, setStep] = useState("choose"); // choose → phone → otp
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [maskedPhone, setMaskedPhone] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const otpRefs = useRef([]);

  const reset = () => {
    setStep("choose");
    setPhone("");
    setName("");
    setOtp(Array(OTP_LENGTH).fill(""));
    setMaskedPhone("");
    setDevOtp("");
    setBusy(false);
    setSecondsLeft(0);
  };

  useEffect(() => {
    if (!open) reset();
  }, [open]);

  // Resend countdown
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [secondsLeft]);

  const sendOtp = async () => {
    const cleaned = phone.replace(/\D+/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      toast.error("Enter a valid 10-digit Indian mobile");
      return;
    }
    setBusy(true);
    try {
      const res = await requestOtp(cleaned);
      setMaskedPhone(res.masked_phone || cleaned);
      setDevOtp(res.dev_otp || "");
      setSecondsLeft(30);
      setStep("otp");
      // Auto-focus first OTP box
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
      toast.success("OTP sent", {
        description: res.dev_otp
          ? `Dev mode — code: ${res.dev_otp} (or use 1234 / 123456)`
          : `Sent to ${res.masked_phone}`,
        duration: 3500,
      });
    } catch (e) {
      toast.error(e?.message || "Couldn't send OTP");
    } finally {
      setBusy(false);
    }
  };

  const setOtpDigit = (idx, raw) => {
    const v = (raw || "").replace(/\D+/g, "");
    if (!v) {
      setOtp((arr) => {
        const next = [...arr];
        next[idx] = "";
        return next;
      });
      return;
    }
    // Handle paste of full code
    if (v.length > 1) {
      const arr = Array(OTP_LENGTH).fill("");
      const chars = v.slice(0, OTP_LENGTH).split("");
      chars.forEach((c, i) => (arr[i] = c));
      setOtp(arr);
      const lastIdx = Math.min(chars.length, OTP_LENGTH) - 1;
      otpRefs.current[lastIdx]?.focus();
      return;
    }
    setOtp((arr) => {
      const next = [...arr];
      next[idx] = v[0];
      return next;
    });
    if (idx < OTP_LENGTH - 1) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
    if (e.key === "Enter") verify();
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length < 4) {
      toast.error("Enter the full code");
      return;
    }
    setBusy(true);
    try {
      const cleaned = phone.replace(/\D+/g, "").slice(-10);
      const res = await verifyOtp(cleaned, code, name.trim() || undefined);
      toast.success("Signed in", {
        description: `Welcome${res?.customer?.name ? `, ${res.customer.name}` : ""}!`,
        duration: 2200,
      });
      onAuthed?.(res?.customer);
      onClose?.();
    } catch (e) {
      toast.error(e?.message || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    startGoogleLogin();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6 bg-black/55 backdrop-blur-sm"
          onClick={onClose}
          data-testid="customer-auth-dialog"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-md bg-white sm:rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.22)] overflow-hidden max-h-[100vh] sm:max-h-[92vh] flex flex-col"
          >
            <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[#E0B53C]/70 to-transparent" />
            <button
              onClick={onClose}
              data-testid="auth-close-btn"
              aria-label="close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 hover:bg-neutral-100 flex items-center justify-center shadow-sm border border-black/5"
            >
              <X size={16} strokeWidth={1.8} />
            </button>

            <div className="p-6 sm:p-8 overflow-y-auto">
              <p className="text-[11px] uppercase tracking-[0.24em] text-[#064E3B] font-semibold">
                Welcome to MastiBazaar
              </p>
              <h2 className="font-display text-3xl sm:text-4xl text-[#0B0C10] mt-1 mb-4 font-light tracking-tight">
                Sign in to continue.
              </h2>

              {step === "choose" && (
                <ChoiceStep
                  onGoogle={handleGoogle}
                  onPhone={() => setStep("phone")}
                />
              )}

              {step === "phone" && (
                <PhoneStep
                  phone={phone}
                  setPhone={setPhone}
                  name={name}
                  setName={setName}
                  busy={busy}
                  onBack={() => setStep("choose")}
                  onContinue={sendOtp}
                />
              )}

              {step === "otp" && (
                <OtpStep
                  otp={otp}
                  setOtpDigit={setOtpDigit}
                  handleOtpKey={handleOtpKey}
                  refs={otpRefs}
                  maskedPhone={maskedPhone}
                  devOtp={devOtp}
                  secondsLeft={secondsLeft}
                  onResend={sendOtp}
                  busy={busy}
                  onBack={() => setStep("phone")}
                  onVerify={verify}
                />
              )}

              <p className="text-[10px] text-neutral-500 text-center mt-6 leading-relaxed">
                By signing in you agree to MastiBazaar's terms and our COD-only
                checkout policy. We never share your details.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ChoiceStep = ({ onGoogle, onPhone }) => (
  <div className="space-y-3" data-testid="auth-choice-step">
    <button
      type="button"
      onClick={onGoogle}
      data-testid="auth-mode-google"
      className="w-full inline-flex items-center justify-center gap-3 bg-white border border-black/15 rounded-full py-3.5 px-5 text-sm font-medium text-[#0B0C10] hover:border-[#0B0C10] hover:-translate-y-0.5 hover:shadow-md transition-all"
    >
      <GoogleGlyph />
      Continue with Google
    </button>
    <div className="flex items-center gap-3 my-2">
      <span className="h-px flex-1 bg-black/10" />
      <span className="text-[10px] uppercase tracking-[0.24em] text-neutral-400">
        or
      </span>
      <span className="h-px flex-1 bg-black/10" />
    </div>
    <button
      type="button"
      onClick={onPhone}
      data-testid="auth-mode-phone"
      className="w-full inline-flex items-center justify-center gap-3 bg-[#022C22] text-[#E0B53C] rounded-full py-3.5 px-5 text-sm font-medium hover:bg-[#053f30] hover:-translate-y-0.5 hover:shadow-md transition-all"
    >
      <Phone size={15} strokeWidth={1.8} />
      Continue with Mobile OTP
    </button>
    <div className="mt-4 flex items-start gap-2 rounded-2xl bg-[#FFF9E6] border border-[#E0B53C]/30 p-3">
      <Sparkles
        size={14}
        strokeWidth={1.8}
        className="text-[#064E3B] mt-0.5 shrink-0"
      />
      <p className="text-xs text-neutral-700 leading-relaxed">
        Pick Google for the fastest one-tap sign-in. Pick mobile for COD
        without an email — we'll send a 6-digit code instantly.
      </p>
    </div>
  </div>
);

const PhoneStep = ({ phone, setPhone, name, setName, busy, onBack, onContinue }) => (
  <div className="space-y-4" data-testid="auth-phone-step">
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#0B0C10]"
    >
      <ChevronLeft size={13} strokeWidth={2} />
      Back
    </button>
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5 font-medium">
        Your name (optional)
      </span>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Aisha Kapoor"
        data-testid="auth-name-input"
        className="w-full bg-white border border-black/10 focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none rounded-xl px-4 py-3 text-sm transition-all"
      />
    </label>
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.18em] text-neutral-600 mb-1.5 font-medium">
        Mobile number
      </span>
      <div className="flex items-stretch overflow-hidden rounded-xl bg-white border border-black/10 focus-within:border-[#0B0C10] focus-within:ring-1 focus-within:ring-[#0B0C10] transition-all">
        <span className="px-3 flex items-center text-sm text-neutral-500 bg-neutral-50 border-r border-black/5 font-medium">
          +91
        </span>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="9876543210"
          inputMode="numeric"
          maxLength={10}
          autoFocus
          data-testid="auth-phone-input"
          onKeyDown={(e) => {
            if (e.key === "Enter") onContinue();
          }}
          className="flex-1 outline-none px-3 py-3 text-sm bg-transparent"
        />
      </div>
    </label>
    <button
      type="button"
      onClick={onContinue}
      disabled={busy}
      data-testid="auth-send-otp-btn"
      className="w-full inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3.5 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60"
    >
      {busy ? (
        <Loader2 size={15} strokeWidth={2} className="animate-spin" />
      ) : (
        <>
          Send OTP <ArrowRight size={15} strokeWidth={1.8} />
        </>
      )}
    </button>
  </div>
);

const OtpStep = ({
  otp,
  setOtpDigit,
  handleOtpKey,
  refs,
  maskedPhone,
  devOtp,
  secondsLeft,
  onResend,
  busy,
  onBack,
  onVerify,
}) => (
  <div className="space-y-4" data-testid="auth-otp-step">
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#0B0C10]"
    >
      <ChevronLeft size={13} strokeWidth={2} />
      Change number
    </button>
    <div className="flex items-start gap-2 rounded-2xl bg-[#D1FFEB]/50 border border-[#064E3B]/15 p-3">
      <ShieldCheck
        size={14}
        strokeWidth={1.8}
        className="text-[#064E3B] mt-0.5 shrink-0"
      />
      <p className="text-xs text-neutral-700">
        We sent a 6-digit code to <strong>{maskedPhone}</strong>. It expires in
        5 minutes.
      </p>
    </div>

    <div className="grid grid-cols-6 gap-2">
      {otp.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          value={d}
          onChange={(e) => setOtpDigit(i, e.target.value)}
          onKeyDown={(e) => handleOtpKey(i, e)}
          inputMode="numeric"
          maxLength={1}
          data-testid={`auth-otp-input-${i}`}
          className="h-12 sm:h-14 text-center font-display text-xl bg-white border border-black/10 rounded-xl focus:border-[#0B0C10] focus:ring-1 focus:ring-[#0B0C10] outline-none transition-all"
        />
      ))}
    </div>

    {devOtp && (
      <p
        className="text-[11px] text-center text-[#064E3B] font-medium"
        data-testid="auth-dev-otp-hint"
      >
        Dev OTP: <span className="font-mono">{devOtp}</span> · (universal test
        codes: <span className="font-mono">1234</span> or{" "}
        <span className="font-mono">123456</span>)
      </p>
    )}

    <button
      type="button"
      onClick={onVerify}
      disabled={busy}
      data-testid="auth-verify-otp-btn"
      className="w-full inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3.5 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60"
    >
      {busy ? (
        <Loader2 size={15} strokeWidth={2} className="animate-spin" />
      ) : (
        <>Verify &amp; sign in</>
      )}
    </button>

    <div className="text-center">
      {secondsLeft > 0 ? (
        <p className="text-xs text-neutral-500">Resend in {secondsLeft}s</p>
      ) : (
        <button
          type="button"
          onClick={onResend}
          disabled={busy}
          data-testid="auth-resend-otp-btn"
          className="text-xs font-medium text-[#0B0C10] hover:underline"
        >
          Resend OTP
        </button>
      )}
    </div>
  </div>
);

// Tiny multi-coloured Google glyph (no external dependency).
const GoogleGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.28 1.4-1.11 2.58-2.36 3.37v2.81h3.81c2.23-2.05 3.57-5.08 3.57-8.42z"
    />
    <path
      fill="#34A853"
      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.81-2.81c-1.06.71-2.41 1.13-4.12 1.13-3.17 0-5.85-2.14-6.81-5.02H1.27v3.15C3.25 21.3 7.31 24 12 24z"
    />
    <path
      fill="#FBBC05"
      d="M5.19 14.39c-.25-.71-.39-1.46-.39-2.39s.14-1.68.39-2.39V6.46H1.27C.46 8.06 0 9.98 0 12s.46 3.94 1.27 5.54l3.92-3.15z"
    />
    <path
      fill="#EA4335"
      d="M12 4.75c1.77 0 3.36.61 4.61 1.81l3.39-3.39C17.95 1.16 15.24 0 12 0 7.31 0 3.25 2.7 1.27 6.46l3.92 3.15C6.15 6.89 8.83 4.75 12 4.75z"
    />
  </svg>
);
